// Hostile-content render decisions for the web reveal path (ADR-0012). All
// revealed content is treated as attacker-controlled: this module decides how a
// Kind is dispositioned and builds the opaque-origin sandbox the content renders
// in. It is pure (no DOM), so the disposition rules are unit-tested directly.
//
// The Fragment Key is NEVER an input here. Decryption happens in the key-holding
// document; only the resulting plaintext bytes flow through these functions and
// into the sandbox, so the key cannot reach the sandbox by construction.
import type { ClipMeta } from "../shared/core";

export type RenderMode = "text" | "code" | "image" | "video" | "audio" | "secret" | "link" | "download";

export interface RenderDecision {
  mode: RenderMode;
  mime?: string;
  filename?: string;
}

// Fatal decoder: throws on invalid UTF-8, so we can tell text from binary.
const tdFatal = new TextDecoder("utf-8", { fatal: true });
// Loose decoder: for display of content already known to be text.
const tdLoose = new TextDecoder();

// True when bytes are clean UTF-8 text with no NUL, so showing them as escaped
// text is sensible rather than forcing a download.
export function looksUtf8(bytes: Uint8Array): boolean {
  if (bytes.includes(0)) return false;
  try {
    tdFatal.decode(bytes);
    return true;
  } catch {
    return false;
  }
}

function isSvg(meta: ClipMeta): boolean {
  return meta.kind === "svg" || (meta.mime ?? "").toLowerCase().includes("svg");
}

// Disposition for a revealed clip (ADR-0012):
// - secret: masked until the viewer chooses to show it, then rendered as text.
// - SVG: always a download, never inline (SVG can carry script).
// - image (non-SVG): rendered in the sandbox.
// - file: download.
// - text: rendered as escaped text in the sandbox (textContent).
// - code: rendered in the sandbox with the inlined highlighter (POOF-11).
//   Highlighting tokenizes the source and writes each token via textContent,
//   so a code payload that contains raw HTML stays inert.
// - url (masked URL, ADR-0013): rendered in the key-holding parent as a
//   clickable anchor, BUT only after safeHttpUrl validates the destination.
//   The sandbox cannot open a new tab, which is why this Kind is the one
//   narrow exception to ADR-0012's sandbox-everything rule. The scheme
//   allowlist (safeHttpUrl) is the load-bearing control that stops a
//   `javascript:` or `data:` payload from running in the parent origin.
// - unknown kind: escaped text if it is valid UTF-8, otherwise a download.
export function decideRender(meta: ClipMeta, bytes: Uint8Array): RenderDecision {
  const kind = meta.kind;
  if (kind === "secret") return { mode: "secret" };
  if (isSvg(meta)) return { mode: "download", mime: "application/octet-stream", filename: meta.filename };
  if (kind === "image" || (meta.mime ?? "").startsWith("image/")) {
    return { mode: "image", mime: meta.mime ?? "application/octet-stream" };
  }
  // Video / audio play inline in the opaque-origin sandbox via a blob URL, the
  // same isolation as images (ADR-0012); the key never enters the sandbox.
  if (kind === "video" || (meta.mime ?? "").startsWith("video/")) {
    return { mode: "video", mime: meta.mime ?? "application/octet-stream" };
  }
  if (kind === "audio" || (meta.mime ?? "").startsWith("audio/")) {
    return { mode: "audio", mime: meta.mime ?? "application/octet-stream" };
  }
  if (kind === "file") return { mode: "download", mime: meta.mime, filename: meta.filename };
  if (kind === "url") return { mode: "link" };
  if (kind === "code") return { mode: "code" };
  if (kind === "text") return { mode: "text" };
  // Unknown kind: safe-as-text when UTF-8, otherwise a download.
  return looksUtf8(bytes)
    ? { mode: "text" }
    : { mode: "download", mime: meta.mime, filename: meta.filename };
}

// Scheme allowlist for the url Kind (ADR-0013). Returns the canonical href
// (URL.href, lowercased scheme, default-port normalization) ONLY when the
// destination parses as a URL and uses http: or https:; otherwise null.
// Any other scheme (javascript:, data:, vbscript:, file:, blob:, ftp:, ws:,
// mailto:, tel:, about:, custom:, ...) yields null, and the renderer falls
// back to escaped-text-in-the-sandbox. The reveal path must never assign an
// href before this returns a non-null value: a `javascript:` href would run
// in the key-holding parent origin and exfiltrate the Fragment Key.
export { safeHttpUrl } from "../shared/create-kind";

export function escapeHtml(s: string): string {
  return s.replace(
    /[&<>"']/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c] as string,
  );
}

// Clamp the reveal iframe height to a sensible range so the sandbox auto-sizes
// to its content without growing to a window-busting size. Used by mountSandbox
// when it receives a `poof-size` message from the opaque-origin sandbox.
// Pure (no DOM), so it is unit-tested directly. NaN/Infinity collapse to `min`
// so an inert size message can never produce an invalid height.
export function clampHeight(raw: number, min: number, max: number): number {
  if (!Number.isFinite(raw)) return min;
  if (max < min) return min;
  return Math.max(min, Math.min(max, Math.round(raw)));
}

// Countdown (ADR-0014). Pure helpers for the recipient-facing remaining-time
// view; the DOM wiring + auto-clear timer live in main.ts. The deadline
// (expiresAt) comes from the decrypted metadata, so these never see the key.

// Human remaining-time string from a millisecond delta. Coarsens with scale:
// hours show "Hh Mm", minutes show "Mm Ss", under a minute shows "Ss". A
// non-positive delta is "0s".
export function formatRemaining(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${sec}s`;
  return `${sec}s`;
}

// Coarse human duration ("5 minutes", "1 hour", "2 days") for the reveal-anchored
// precard hint (ADR-0017): "this poof's timer starts when you reveal it, then X".
// Picks the largest unit at or below the duration and rounds, so the common TTL
// presets read cleanly and off-grid customs stay approximate. Pure, unit-tested.
export function formatDuration(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  const units: [string, number][] = [
    ["day", 86_400],
    ["hour", 3_600],
    ["minute", 60],
    ["second", 1],
  ];
  for (const [name, secs] of units) {
    if (s >= secs) {
      const n = Math.round(s / secs);
      return `${n} ${name}${n === 1 ? "" : "s"}`;
    }
  }
  return "0 seconds";
}

// Fraction (0..1) of the viewing window still remaining, for the depleting bar.
// The window is measured from when the page opened (openedAt), not the original
// TTL: the countdown needs only the time left, never the total duration
// (ADR-0014). Clamped to [0,1]; a non-positive or already-past window yields 0.
export function countdownFraction(now: number, openedAt: number, expiresAt: number): number {
  const total = expiresAt - openedAt;
  const left = expiresAt - now;
  if (total <= 0 || left <= 0) return 0;
  if (left >= total) return 1;
  return left / total;
}

// Message posted into the sandbox. It carries only displayable plaintext (text
// or code) or raw image bytes plus a mime, never the key. The text and code
// branches send a string the sandbox renders via textContent (text) or via the
// inlined highlighter (code, still token-by-token textContent). The image
// branch sends bytes the sandbox wraps in a blob URL.
export type SandboxMessage =
  | { type: "poof-render"; mode: "text"; text: string }
  | { type: "poof-render"; mode: "code"; text: string }
  | { type: "poof-render"; mode: "image"; bytes: ArrayBuffer; mime: string }
  | { type: "poof-render"; mode: "video"; bytes: ArrayBuffer; mime: string }
  | { type: "poof-render"; mode: "audio"; bytes: ArrayBuffer; mime: string };

// The plaintext bytes flow into the opaque-origin sandbox document
// (public/sandbox.html) through this message only. The sandbox renders text via
// textContent (never HTML), code via the inlined highlighter (token text still
// rendered via textContent), or an image via a blob URL. The key is never here.
export function buildSandboxMessage(decision: RenderDecision, bytes: Uint8Array): SandboxMessage {
  if (decision.mode === "image" || decision.mode === "video" || decision.mode === "audio") {
    // Copy into a standalone ArrayBuffer so we never transfer (and detach) the
    // caller's cached bytes. The sandbox wraps these in a blob URL.
    const copy = bytes.slice();
    return {
      type: "poof-render",
      mode: decision.mode,
      bytes: copy.buffer,
      mime: decision.mime ?? "application/octet-stream",
    };
  }
  if (decision.mode === "code") {
    return { type: "poof-render", mode: "code", text: tdLoose.decode(bytes) };
  }
  // text and shown-secret both render as escaped text.
  return { type: "poof-render", mode: "text", text: tdLoose.decode(bytes) };
}
